import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@teivor/shared-ui';

import { useMeQuery } from '../features/settings/queries';

import { PageLoader } from './RouteProgress';

/**
 * Auth gate for the staff app shell.
 *
 * `AppLayout` renders the entire shell UNCONDITIONALLY — it reads `me` for the
 * avatar/role but never gates on it. So when `GET /api/iam/auth/me` was
 * unresolved or errored, the shell painted with no identity: the "logged-out
 * shell with a '?' avatar where nothing works" bug (present on staging + prod).
 *
 * This wrapper sits around ONLY the `AppLayout` route group (never around
 * `/login` or `/auth/*`, which live under `AuthLayout`), so a redirect to
 * `/login` unmounts the gate and can never loop.
 *
 * Branching order is deliberate — presence of `me.data` is checked FIRST so a
 * cached profile always wins and the loader never flashes on a background
 * (30s-stale) refetch or on cached SPA navigation between app routes:
 *   1. `me.data`      → render the shell (incl. a stale profile React Query keeps
 *                       across a failed background refetch).
 *   2. `me.isPending` → full-page loader. Covers the true cold first load AND the
 *                       401→refresh-failed case, where the api client fires a
 *                       `window.location.assign('/login')` and returns a
 *                       never-resolving promise, so the query stays pending until
 *                       that navigation completes (bounded).
 *   3. `me.isError`   → a non-401 `/me` failure (500 / network) the api client
 *                       does NOT redirect for. Show a recoverable screen (retry
 *                       preserves the session; sign-in for a real auth loss)
 *                       instead of the dead shell.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const me = useMeQuery();

  if (me.data) return <>{children}</>;
  if (me.isPending) return <PageLoader />;

  const goToLogin = () => {
    const ret = window.location.pathname + window.location.search;
    window.location.assign(`/login?return=${encodeURIComponent(ret)}`);
  };

  return (
    <div
      className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 px-6 text-center"
      style={{ fontFamily: 'var(--tvl-font-ui, "Hanken Grotesk", sans-serif)' }}
    >
      <h1 className="text-lg font-semibold text-[var(--tvl-ink)]">
        {t('auth.session_error_title', 'Nepodařilo se načíst účet')}
      </h1>
      <p className="max-w-md text-sm leading-5 text-[var(--tvl-ink-2)]">
        {t(
          'auth.session_error_desc',
          'Něco se pokazilo při načítání vašeho účtu. Zkuste to prosím znovu; pokud potíže přetrvávají, přihlaste se znovu.',
        )}
      </p>
      <div className="flex gap-3">
        <Button variant="primary" onClick={() => void me.refetch()} loading={me.isFetching}>
          {t('common.try_again', 'Zkusit znovu')}
        </Button>
        <Button variant="tertiary" onClick={goToLogin}>
          {t('auth.sign_in', 'Přihlásit se')}
        </Button>
      </div>
    </div>
  );
}
